import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPieChart, FiClock, FiInfo, FiCheckCircle, FiChevronDown, FiChevronUp, FiShield, FiCheck, FiEdit2, FiSave, FiRefreshCw, FiAlertCircle } = FiIcons;

const GoalAlignedPortfolio = ({ goals, userProfile }) => {
  const [showDisclaimers, setShowDisclaimers] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [portfolioData, setPortfolioData] = useState([]);
  const [totalCalculatedSIP, setTotalCalculatedSIP] = useState(0);

  // Helper: Calculate SIP for a single goal
  const calculateGoalSIP = (goal) => {
    const years = parseInt(goal.yearsFromNow);
    const currentCost = parseInt(goal.currentCost);
    
    // Inflation Logic matching GoalSummaryTable
    let inflationRate = 6.5;
    const lowerName = goal.name.toLowerCase();
    if (lowerName.includes('education')) inflationRate = 10.5;
    else if (lowerName.includes('health') || lowerName.includes('medical')) inflationRate = 9.5;

    const futureValue = currentCost * Math.pow(1 + inflationRate / 100, years);
    
    // Expected Return Logic matching Results
    const expectedReturn = goal.expectedReturn 
      ? parseFloat(goal.expectedReturn) 
      : (years >= 10 ? 12 : years > 3 ? 10 : 7);

    const monthlyReturn = expectedReturn / 100 / 12;
    const months = years * 12;
    
    if (monthlyReturn === 0) return futureValue / months;
    return (futureValue * monthlyReturn) / (Math.pow(1 + monthlyReturn, months) - 1);
  };

  // STEP 1: Build Portfolio Bottom-Up based on Goal Needs
  const buildGoalBasedPortfolio = () => {
    let shortTermSIP = 0;
    let mediumTermSIP = 0;
    let longTermSIP = 0;

    const shortTermGoals = [];
    const mediumTermGoals = [];
    const longTermGoals = [];

    // 1. Aggregate SIP requirements by Time Horizon
    goals.forEach(goal => {
      const sip = calculateGoalSIP(goal);
      const years = parseInt(goal.yearsFromNow);

      if (years <= 3) {
        shortTermSIP += sip;
        shortTermGoals.push(goal.name);
      } else if (years <= 9) {
        mediumTermSIP += sip;
        mediumTermGoals.push(goal.name);
      } else {
        longTermSIP += sip;
        longTermGoals.push(goal.name);
      }
    });

    const totalSIP = shortTermSIP + mediumTermSIP + longTermSIP;
    setTotalCalculatedSIP(totalSIP);

    const categories = [];

    // 2. Distribute Short Term SIP (100% Debt/Liquid)
    if (shortTermSIP > 0) {
      // Split between Liquid (very short) and Short Duration
      // If we have 0-1yr goals, prioritize Liquid. For simplicity, we'll do a safe split.
      const liquidShare = 0.4; 
      
      categories.push({
        id: 'debt-liquid',
        name: 'Liquid / Ultra-short Fund',
        amount: Math.round(shortTermSIP * liquidShare),
        allocation: 0, // Calculated later
        purpose: 'Immediate Liquidity & Safety',
        goalsSupported: shortTermGoals.join(', '),
        timeHorizon: '0–1 yr'
      });

      categories.push({
        id: 'debt-short',
        name: 'Short Duration Debt Fund',
        amount: Math.round(shortTermSIP * (1 - liquidShare)),
        allocation: 0,
        purpose: 'Capital Preservation',
        goalsSupported: shortTermGoals.join(', '),
        timeHorizon: '1–3 yrs'
      });
    }

    // 3. Distribute Medium Term SIP (100% Hybrid)
    if (mediumTermSIP > 0) {
      categories.push({
        id: 'hybrid',
        name: 'Balanced Advantage / Aggressive Hybrid',
        amount: Math.round(mediumTermSIP),
        allocation: 0,
        purpose: 'Stability + Moderate Growth',
        goalsSupported: mediumTermGoals.join(', '),
        timeHorizon: '3–9 yrs'
      });
    }

    // 4. Distribute Long Term SIP (Equity Split based on Risk Profile)
    if (longTermSIP > 0) {
      const risk = userProfile.riskComfort || 'Medium';
      let equitySplit = { large: 0.5, flexi: 0.5, mid: 0 }; // Default Medium

      if (risk === 'Low') {
        equitySplit = { large: 0.7, flexi: 0.3, mid: 0 };
      } else if (risk === 'High') {
        equitySplit = { large: 0.3, flexi: 0.4, mid: 0.3 };
      }

      // Large Cap
      if (equitySplit.large > 0) {
        categories.push({
          id: 'large-cap',
          name: 'Large Cap Equity',
          amount: Math.round(longTermSIP * equitySplit.large),
          allocation: 0,
          purpose: 'Long-term Stability',
          goalsSupported: longTermGoals.join(', '),
          timeHorizon: '10+ yrs'
        });
      }

      // Flexi Cap
      if (equitySplit.flexi > 0) {
        categories.push({
          id: 'flexi-cap',
          name: 'Flexi Cap / Index Fund',
          amount: Math.round(longTermSIP * equitySplit.flexi),
          allocation: 0,
          purpose: 'Core Wealth Creation',
          goalsSupported: longTermGoals.join(', '),
          timeHorizon: '10+ yrs'
        });
      }

      // Mid Cap
      if (equitySplit.mid > 0) {
        categories.push({
          id: 'mid-cap',
          name: 'Mid & Small Cap Equity',
          amount: Math.round(longTermSIP * equitySplit.mid),
          allocation: 0,
          purpose: 'High Growth Potential',
          goalsSupported: longTermGoals.join(', '),
          timeHorizon: '15+ yrs'
        });
      }
    }

    // Fallback if empty
    if (categories.length === 0) {
       categories.push({
        id: 'default',
        name: 'Flexi Cap Fund',
        amount: 5000,
        allocation: 100,
        purpose: 'General Wealth Creation',
        goalsSupported: 'Future Goals',
        timeHorizon: '5+ yrs'
       });
    }

    // 5. Calculate Allocation Percentages
    const finalTotal = categories.reduce((sum, c) => sum + c.amount, 0);
    return categories.map(cat => ({
      ...cat,
      allocation: finalTotal > 0 ? Math.round((cat.amount / finalTotal) * 100) : 0
    }));
  };

  // Initialize data
  useEffect(() => {
    setPortfolioData(buildGoalBasedPortfolio());
  }, [goals, userProfile]);

  // Handle Editing
  const handleInputChange = (index, field, value) => {
    const updatedData = [...portfolioData];
    
    if (field === 'allocation') {
        // If allocation changes, update amount
        const newAlloc = parseFloat(value) || 0;
        updatedData[index] = { 
            ...updatedData[index], 
            allocation: newAlloc,
            amount: Math.round(totalCalculatedSIP * (newAlloc / 100))
        };
    } else if (field === 'amount') {
        // If amount changes, update allocation
        const newAmount = parseInt(value) || 0;
        const total = totalCalculatedSIP || 1; // avoid div by zero
        updatedData[index] = {
            ...updatedData[index],
            amount: newAmount,
            allocation: Math.round((newAmount / total) * 100)
        };
    } else {
        updatedData[index] = { ...updatedData[index], [field]: value };
    }

    setPortfolioData(updatedData);
  };

  const resetToRecommended = () => {
    setPortfolioData(buildGoalBasedPortfolio());
    setIsEditing(false);
  };

  const totalAllocation = portfolioData.reduce((sum, item) => sum + (parseFloat(item.allocation) || 0), 0);
  const totalAmount = portfolioData.reduce((sum, item) => sum + (parseInt(item.amount) || 0), 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-xl shadow-lg p-6 mb-8 break-inside-avoid"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div className="flex items-center space-x-3">
          <SafeIcon icon={FiPieChart} className="text-2xl text-indigo-600" />
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Goal-Aligned Portfolio</h2>
            <p className="text-sm text-gray-600 mt-1">Allocations are strictly based on goal time horizons</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 no-print">
            {isEditing ? (
                <>
                    <button 
                        onClick={() => setIsEditing(false)} 
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                        <SafeIcon icon={FiSave} />
                        <span>Save Changes</span>
                    </button>
                    <button 
                        onClick={resetToRecommended} 
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                    >
                        <SafeIcon icon={FiRefreshCw} />
                        <span>Reset</span>
                    </button>
                </>
            ) : (
                <button 
                    onClick={() => setIsEditing(true)} 
                    className="flex items-center space-x-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm"
                >
                    <SafeIcon icon={FiEdit2} />
                    <span>Customize Table</span>
                </button>
            )}
        </div>
      </div>

      {/* Portfolio Allocation Table */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg p-4 flex justify-between items-center">
        <h3 className="text-lg font-bold">Recommended Fund Mix</h3>
        {isEditing && <span className="text-xs bg-white/20 px-2 py-1 rounded">Editing Mode</span>}
      </div>
      
      <div className="border border-gray-200 border-t-0 rounded-b-lg overflow-hidden mb-8 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-[20%]">Category</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-[10%]">Alloc %</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider w-[15%]">Amount (₹)</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-[20%]">Primary Purpose</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-[25%]">Goals Supported</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-[10%]">Horizon</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {portfolioData.map((category, index) => (
                <tr key={category.id || index} className={isEditing ? "bg-blue-50/30" : "hover:bg-gray-50 transition-colors"}>
                  {/* Category Name */}
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">
                    {isEditing ? (
                        <input 
                            type="text" 
                            value={category.name} 
                            onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                            className="w-full p-1 border border-indigo-300 rounded focus:ring-1 focus:ring-indigo-500"
                        />
                    ) : category.name}
                  </td>

                  {/* Allocation % */}
                  <td className="px-4 py-4 text-sm text-center">
                     {isEditing ? (
                        <div className="flex items-center justify-center">
                            <input 
                                type="number" 
                                value={category.allocation} 
                                onChange={(e) => handleInputChange(index, 'allocation', e.target.value)}
                                className="w-16 p-1 text-center border border-indigo-300 rounded focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>
                    ) : (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${category.allocation > 0 ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-500'}`}>
                        {category.allocation}%
                        </span>
                    )}
                  </td>

                  {/* Amount */}
                  <td className="px-4 py-4 text-sm text-right font-mono font-medium text-gray-700">
                    {isEditing ? (
                        <input 
                            type="number" 
                            value={category.amount} 
                            onChange={(e) => handleInputChange(index, 'amount', e.target.value)}
                            className="w-full p-1 text-right border border-indigo-300 rounded focus:ring-1 focus:ring-indigo-500"
                        />
                    ) : (
                        `₹${category.amount.toLocaleString('en-IN')}`
                    )}
                  </td>

                  {/* Purpose */}
                  <td className="px-4 py-4 text-sm text-gray-600">
                     {isEditing ? (
                        <input 
                            type="text" 
                            value={category.purpose} 
                            onChange={(e) => handleInputChange(index, 'purpose', e.target.value)}
                            className="w-full p-1 border border-indigo-300 rounded focus:ring-1 focus:ring-indigo-500"
                        />
                    ) : category.purpose}
                  </td>

                  {/* Goals Supported */}
                  <td className="px-4 py-4 text-sm text-gray-600">
                     {isEditing ? (
                        <textarea 
                            value={category.goalsSupported} 
                            onChange={(e) => handleInputChange(index, 'goalsSupported', e.target.value)}
                            className="w-full p-1 border border-indigo-300 rounded focus:ring-1 focus:ring-indigo-500 text-xs"
                            rows={2}
                        />
                    ) : category.goalsSupported}
                  </td>

                  {/* Time Horizon */}
                  <td className="px-4 py-4 text-sm text-center text-gray-600">
                     {isEditing ? (
                        <input 
                            type="text" 
                            value={category.timeHorizon} 
                            onChange={(e) => handleInputChange(index, 'timeHorizon', e.target.value)}
                            className="w-full p-1 text-center border border-indigo-300 rounded focus:ring-1 focus:ring-indigo-500"
                        />
                    ) : category.timeHorizon}
                  </td>
                </tr>
              ))}
              
              {/* Totals Row */}
              <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                <td className="px-4 py-3 text-sm text-gray-900">TOTAL</td>
                <td className="px-4 py-3 text-sm text-center text-gray-900">
                  <span className={`${totalAllocation !== 100 ? 'text-red-600' : 'text-green-600'}`}>
                    {totalAllocation}%
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-right text-gray-900">
                    ₹{totalAmount.toLocaleString('en-IN')}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600" colSpan="3">
                    {totalAllocation !== 100 && isEditing ? (
                        <span className="text-red-500 flex items-center text-xs">
                            <SafeIcon icon={FiAlertCircle} className="mr-1" />
                            Allocation should equal 100%
                        </span>
                    ) : (
                        "Fully aligned with your specific goal needs"
                    )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Logic Explanation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
          <SafeIcon icon={FiInfo} className="mr-2" />
          How We Calculated This
        </h4>
        <p className="text-blue-800 text-sm">
          Unlike generic calculators that just give you a 60:40 split, this allocation is built <strong>bottom-up</strong> from your specific goals. 
          If you have high Short-Term requirements (e.g., buying a car in 2 years), your Debt allocation will automatically be higher to ensure that money is safe.
          Long-term goals are assigned to Equity for growth.
        </p>
      </div>

      {/* Collapsible Disclaimers & Guidelines */}
      <div className="border border-indigo-100 rounded-lg overflow-hidden bg-white shadow-sm no-print">
        <button
          onClick={() => setShowDisclaimers(!showDisclaimers)}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        >
          <div className="flex items-center space-x-2 font-semibold text-gray-800">
            <SafeIcon icon={FiShield} className="text-indigo-500" />
            <span>Important Disclaimers & Rebalancing Guidelines</span>
          </div>
          <SafeIcon icon={showDisclaimers ? FiChevronUp : FiChevronDown} className="text-gray-500" />
        </button>
        <AnimatePresence>
          {showDisclaimers && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-gray-100"
            >
              <div className="p-6 bg-yellow-50/50">
                {/* Disclaimers */}
                <div className="mb-6">
                  <h4 className="font-semibold text-yellow-900 mb-3 flex items-center">
                    <SafeIcon icon={FiInfo} className="mr-2" />
                    Important Disclaimers
                  </h4>
                  <ul className="space-y-2 text-sm text-yellow-800">
                    <li className="flex items-start space-x-2">
                      <SafeIcon icon={FiCheck} className="text-yellow-600 text-xs mt-1 flex-shrink-0" />
                      <span>The fund recommendations mentioned above are for educational purposes only and do not constitute investment advice.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <SafeIcon icon={FiCheck} className="text-yellow-600 text-xs mt-1 flex-shrink-0" />
                      <span>These are sample allocations and should be customized based on your risk appetite, financial situation, and market conditions.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <SafeIcon icon={FiCheck} className="text-yellow-600 text-xs mt-1 flex-shrink-0" />
                      <span>Performance of mutual funds is subject to market risk. Past performance does not guarantee future results.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default GoalAlignedPortfolio;